import React, {useCallback, useState} from "react";
import {observer} from "mobx-react-lite";
import TableRow from "../../../../components/table/components/table-row/table-row";
import Table from "../../../../components/table/table";
import {IColumn} from "../../../../components/table/table.types";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import {FONT_VARIANT} from "../../../../components/fonts/font-props";
import {ButtonIcon} from "../../../../components/inputs/button-icon/button-icon";
import IconReceipt from "../../../../components/icons/icon-receipt/icon-receipt";
import BillingTransactionStore from "../../stores/billing-transaction-store";
import {decimalFormat} from "../../../../services/helpers";
import ChargedAmount from "../charged-amount/charged-amount";
import {EXCESS_USAGE_STATUS} from "../../../../services/api/billing-service";
import BillingDetailsModal from "../details-modal/details-modal";
import planChargesBillingTableStore from "../../stores/plan-charges-billing-store";
import { createPortal } from "react-dom";
import ReportBoxLayout from "../../../../components/layouts/report-box-layout/report-box-layout";
import Report from "../../../../components/report/report";
import { REPORT_TYPES } from "../../../../services/api/report-service";

const planChargesBillingHistoryColumns: IColumn[] = [
    {
        content: "month",
        sortBy: "period_start",
        sortable: true,
        width: {value: 0.6, type: "fr"},
    },
    {
        content: "plan",
        width: {value: 1, type: "fr"},
    },
    {
        content: "amount",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "returns quota",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "returns usage",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "plan price",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "excess usage price",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "excess usage charge",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "addons",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "total",
        width: {value: 0.5, type: "fr"},
    },
    {
        content: "details",
        width: {value: 0.5, type: "fr"},
    },
];

interface IPlanChargesBillingTableCardProps {
    buttonContainerRef: React.RefObject<HTMLDivElement>;
}

const PlanChargesBillingTableCard: React.FC<IPlanChargesBillingTableCardProps> = observer(({buttonContainerRef}) => {
    const [selectedBillingTransaction, setSelectedBillingTransaction] = useState<BillingTransactionStore | undefined>(undefined);
    
    const onGetPlanChargesReportFilters = useCallback(() => ({
        'order_by': planChargesBillingTableStore.sortBy,
        'order_dir': planChargesBillingTableStore.sortDir,
    }), []);

    return (
        <>
            <Table
                isLoading={planChargesBillingTableStore.isLoading}
                columns={planChargesBillingHistoryColumns}
                pagination={{
                    onSetPage: planChargesBillingTableStore.loadPage,
                    page: planChargesBillingTableStore.page,
                    total: planChargesBillingTableStore.total,
                    perPage: planChargesBillingTableStore.perPage,
                }}
                sort={{
                    onSort: planChargesBillingTableStore.sortData,
                    by: planChargesBillingTableStore.sortBy,
                    dir: planChargesBillingTableStore.sortDir,
                }}
                totalResultsTitle="Transactions"
            >
                {planChargesBillingTableStore.list.map((item: BillingTransactionStore) => (
                    <TableRow
                        key={item.plan_payments_total + item.month + item.rmaQuota + item.parentCharged + item.parentStore}
                        cells={[
                            item.month,
                            item.plan_name,
                            `$${decimalFormat(item.planPrice)}`,
                            item.rmaQuota,
                            item.rmaCount,
                            `$${item.parentPlanPrice ? decimalFormat(item.parentPlanPrice) : decimalFormat(item.planPrice)}`,
                            item.pricePerExtraRma ? `$${item.pricePerExtraRma}` : 'N/A',
                            item.excessRmaCharge !== undefined ? (
                                <ChargedAmount
                                    value={`$${decimalFormat(item.excessRmaCharge)} (${item.excessRmas})`}
                                    notCharged={item.parentHasCharge && item.status === EXCESS_USAGE_STATUS.NOT_CHARGED}
                                />
                            ) : 'N/A',
                            `$${decimalFormat(item.plan_addons_total)}`,
                            <FontBody2 variant={FONT_VARIANT.BOLD}>${`${decimalFormat(item.total)}`}</FontBody2>,
                            <ButtonIcon Component={IconReceipt} onClick={() => setSelectedBillingTransaction(item)}/>,
                        ]}
                    />
                ))}
            </Table>

            {
                selectedBillingTransaction &&
                <BillingDetailsModal
                    transaction={selectedBillingTransaction}
                    onClose={() => setSelectedBillingTransaction(undefined)}
                />
            }

            {
                buttonContainerRef.current &&
                createPortal(
                    <ReportBoxLayout>
                        <Report
                            type={REPORT_TYPES.BILLING}
                            title='report'
                            getReportFilters={onGetPlanChargesReportFilters}
                        />
                    </ReportBoxLayout>
                , buttonContainerRef.current)
            }
        </>
    );
});

export default PlanChargesBillingTableCard;
